import React, { FunctionComponent, useMemo, useEffect, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import {
  MdOutlineError,
  MdOutlineInfo,
  MdOutlineQuestionMark,
  MdOutlineWarning,
  MdError,
  MdInfo,
  MdQuestionMark,
  MdWarning,
} from "react-icons/md";
import {
  TbClockFilled,
  TbCalendar,
} from "react-icons/tb";
import {
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Stat,
  StatGroup,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";

import dayjs from "dayjs";
import "dayjs/locale/cs";
import "dayjs/locale/en";

import { useMutation, useLazyQuery } from "@apollo/client";
import { ACK_ALARM_TYPES_MUTATION } from "graphql/mutations";
import { SITE_ALARM_TYPE_QUERY } from "graphql/queries";
import {
  SiteAlarmTypes_siteAlarmTypes,
  SiteAlarmType,
  SiteAlarmType_siteAlarmType,
  SiteAlarmType_siteAlarmType_events,
  SiteAlarmType_siteAlarmType_history,
  SiteAlarmTypeVariables,
  AckAlarmTypes,
  AckAlarmTypesVariables,
} from "graphql/generated";

import { Timestamp, RelativeTimestamp } from "common/hooks/time";
import { useLiveMeasurements } from "scenes/site/hooks";

import AlarmEvents from "./AlarmEvents";
import AlarmHistory from "./AlarmHistory";
import AlarmRules from "./AlarmRules";


interface IProps {
  timezone: string;
  siteId: string;
  item?: SiteAlarmTypes_siteAlarmTypes;
  onClosed?: () => void;
}


const AlarmTypeDetail: FunctionComponent<IProps> = ({
  siteId,
  item,
  timezone,
  onClosed,
}) => {
  const intl = useIntl();
  const bucketName = `alarm_type_${item?.condition}`;
  const { data: liveData } = useLiveMeasurements(siteId, item ? bucketName : null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [fetchAlarmType, { data: fetchedData }] = useLazyQuery<SiteAlarmType, SiteAlarmTypeVariables>(SITE_ALARM_TYPE_QUERY, {
    fetchPolicy: "network-only",
  });
  const data = useMemo<SiteAlarmType_siteAlarmType>(() => ({
    ...item,
    ...fetchedData?.siteAlarmType,
    ...liveData[bucketName],
  }), [item, fetchedData, liveData, bucketName]);
  const [ ackAlarmTypesMutation, { loading: saving } ] = useMutation<AckAlarmTypes, AckAlarmTypesVariables>(ACK_ALARM_TYPES_MUTATION);
  const [ acknowledged, setAcknowledged ] = useState<boolean>(false);
  const textColor = useColorModeValue("black", "white");
  const bgColor = useColorModeValue("white", "black");
  const okColor = useColorModeValue("enposol.300", "enposol.600");
  const warningColor = useColorModeValue("yellow.300", "yellow.600");
  const errorColor = useColorModeValue("red.300", "red.600");
  const infoColor = useColorModeValue("blue.300", "blue.600");
  const noColor = useColorModeValue("gray.300", "gray.600");
  const itemLevelColor = useMemo(() => {
    switch(item?.level) {
      case "ERROR": return errorColor;
      case "WARNING": return warningColor;
      case "INFO": return infoColor;
      default: return noColor;
    }
  }, [item?.level, errorColor, warningColor, infoColor, noColor]);
  const closeModal = useCallback(() => {
    onClose();
    onClosed && onClosed();
  }, [onClose, onClosed]);
  const formatId = (id: string) => id.slice(0, 8);
  const formatRate = useCallback((rate: number): string => {
    if (rate > 120) return `${(rate / 60).toFixed(1)}/m`
    else if (rate > 0) return `${rate.toFixed(1)}/h`
    else return `-`
  }, []);
  const itemLevelIcon = useMemo(() => {
    switch(item?.level) {
      case "ERROR": return [MdError, MdOutlineError];
      case "WARNING": return [MdWarning, MdOutlineWarning];
      case "INFO": return [MdInfo, MdOutlineInfo];
      default: return [MdQuestionMark, MdOutlineQuestionMark];
    }
  }, [item?.level]);
  const itemLevelIntl = useMemo(() => {
    switch(item?.level) {
      case "ERROR": return intl.formatMessage({
        id: "alarms__level__error",
        defaultMessage: "Error",
      });
      case "WARNING": return intl.formatMessage({
        id: "alarms__level__warning",
        defaultMessage: "Warning",
      });
      case "INFO": return intl.formatMessage({
        id: "alarms__level__info",
        defaultMessage: "Info",
      });
      default: return item?.level;
    }
  }, [intl, item?.level]);
  const lookActive = useMemo<boolean>(() => data.active && !acknowledged, [data.active, acknowledged]);
  // const lookMuted = useMemo<boolean>(() => data.muted, [data.muted]);
  const lookUnAcked = useMemo<boolean>(() => !data.active && !data.ack && !acknowledged, [data.active, data.ack, acknowledged]);
  const lookAcked = useMemo<boolean>(() => data.active && data.ack && acknowledged, [data.active, data.ack, acknowledged]);

  const acknowledgeAlarm = useCallback(() => {
    ackAlarmTypesMutation({
      variables: {
        siteId,
        conditionId: data.condition,
        alarmTypes: {
          alarmTypeIds: [
            data.id,
          ],
        },
      },
      onCompleted: () => {
        setAcknowledged(true);
        fetchAlarmType({ variables: { siteId, alarmTypeId: data.condition } });
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.condition, data.id]);

  dayjs.locale(intl.locale);

  useEffect(() => {
    if (item) {
      onOpen();
    } 
  }, [item, onOpen]);

  useEffect(() => {
    setAcknowledged(data ? data.ack : false);
  }, [isOpen, data, data.ack]);

  return (
    <>
      {item && <>
        <Modal
          isOpen={isOpen}
          onClose={closeModal}
          size="6xl"
          scrollBehavior="inside"
        >
          <ModalOverlay backdropFilter="auto" backdropBlur="5px" backdropBrightness="95%" />
          <ModalContent>
            <ModalHeader
              bg={lookUnAcked ? undefined : lookActive ? itemLevelColor : okColor}
              borderTopStartRadius={6}
              borderTopEndRadius={6}
              borderBottomWidth={lookUnAcked && !lookActive ? 5 : undefined}
              borderBottomColor={lookUnAcked && !lookActive ? itemLevelColor : undefined}
            >
              <Stack direction="row" alignItems="stretch" justifyContent="space-between" spacing={3} mr={10}>
                <Stack direction="row" spacing={2} alignItems="baseline" mr={8}>
                  <Icon
                    as={itemLevelIcon[lookActive ? 0 : 1]}
                    fontSize="3xl"
                    title={itemLevelIntl}
                  />
                  <Text fontSize="5xl" title={intl.formatMessage({
                    id: "alarms__alarm_type__abbr",
                    defaultMessage: "Alarm type abbreviation",
                  })}>
                    {item.abbr}
                  </Text>
                  <Text fontSize="3xl" fontWeight="normal" pl={5} title={intl.formatMessage({
                    id: "alarms__alarm_type__level",
                    defaultMessage: "Severity level",
                  })}>
                    {itemLevelIntl}
                  </Text>
                  <Text fontSize="3xl" fontWeight="bold" title={intl.formatMessage({
                    id: "alarms__alarm_type__name",
                    defaultMessage: "Alarm type name",
                  })}>
                    {item.name}
                  </Text>
                </Stack>
                <Stack direction="row" spacing={3} alignItems="center">
                  {(lookUnAcked || lookActive || lookAcked) && <Button
                    alignSelf="center"
                    colorScheme="enposol"
                    onClick={acknowledgeAlarm}
                    disabled={lookAcked}
                    leftIcon={lookAcked ? <CheckCircleIcon/> : saving ? <Spinner size="xs"/> : undefined}
                    boxShadow={`0 0 0 3px ${bgColor}`}
                  >
                    
                    {
                      acknowledged
                      ? intl.formatMessage({
                        id: "alarms__alarm_type__acknowledged",
                        defaultMessage: "Acknowledged active alarm",
                      })
                      : intl.formatMessage({
                        id: "alarms__alarm_type__acknowledge",
                        defaultMessage: "Acknowledge",
                      })
                    }
                  </Button>}
                </Stack>
              </Stack>
            </ModalHeader>

            <ModalCloseButton
              top={3}
              title={intl.formatMessage({
                id: "alarms__alarm_type__detail_close",
                defaultMessage: "Close alarm type detail",
              })}
            />

            <ModalBody>

              {data ? <Stack direction="column" spacing={6} mt={5}>
                {/* <Stack direction="column">
                  <StatGroup flexGrow={1}>
                    {data?.startTime && <Stat>
                      <StatLabel>
                        {intl.formatMessage({
                          id: "alarms__alarm_type__first_event",
                          defaultMessage: "First alarm event",
                        })}
                      </StatLabel>
                      <StatNumber>
                        <RelativeTimestamp date={`${data?.startTime}+00:00`} />
                      </StatNumber>
                      <StatHelpText>
                        <Stack direction="row" alignItems="bottom" spacing={1}>
                          <TbCalendar size={20} />
                          <Text><Timestamp date={`${data?.startTime}+00:00`} /></Text>
                        </Stack>
                      </StatHelpText>
                    </Stat>}

                    <Stat>
                      <StatLabel>
                        {data?.endTime || !data?.startTime
                          ? intl.formatMessage({
                            id: "alarms__alarm_type__latest_incident",
                            defaultMessage: "Latest incident",
                          })
                        : intl.formatMessage({
                            id: "alarms__alarm_type__last_event",
                            defaultMessage: "Last alarm event",
                          })
                        }
                      </StatLabel>
                      <StatNumber>
                        {!data?.lastTime
                          ? intl.formatMessage({
                            id: "alarms__alarm_type__latest_incident_never",
                            defaultMessage: "Never",
                          })
                          : <RelativeTimestamp date={`${data?.lastTime}+00:00`} />
                        }
                      </StatNumber>
                      {data?.lastTime && <StatHelpText>
                        <Stack direction="row" spacing={1} alignItems="bottom">
                          <TbClockFilled size={20} />
                          <Text><Timestamp date={`${data?.lastTime}+00:00`} /></Text>
                        </Stack>
                      </StatHelpText>}
                    </Stat>

                    {data?.active && <Stat>
                      <StatLabel>
                        {intl.formatMessage({
                          id: "alarms__alarm_type__alarm_rate",
                          defaultMessage: "Alarm rate",
                        })}
                      </StatLabel>
                      <StatNumber>
                        {formatRate(data?.rate)}
                      </StatNumber>
                    </Stat>}
                  </StatGroup>
                </Stack> */}
                <Stack direction="row" flexGrow={1} spacing={6}>
                  <Stack direction="column" flexBasis="60%" spacing={6}>
                    {data?.active && <AlarmEvents
                      count={data?.count}
                      items={data?.events as SiteAlarmType_siteAlarmType_events[]}
                      onlyActive={true}
                      timezone={timezone}
                      size="sm"
                      variant="striped"
                    />}
                    <AlarmHistory
                      siteId={siteId}
                      conditionId={item?.condition}
                      items={data?.history as SiteAlarmType_siteAlarmType_history[]}
                      historyLength={data?.historyLength}
                      timezone={timezone}
                      size="sm"
                      variant="striped"
                    />
                  </Stack>
                  <AlarmRules
                    siteId={siteId}
                    alarmType={item?.condition}
                    namespace={item?.ns}
                    textColor={textColor}
                    bgColor={bgColor}
                    flexBasis="40%"
                  />
                </Stack>
              </Stack>
              :
              <Spinner size="lg" m="4" />}

            </ModalBody>
            <ModalFooter>


            </ModalFooter>
          </ModalContent>
        </Modal>
      </>}
    </>
  );
};

export default AlarmTypeDetail;
